import { useState, useEffect } from "react";
import { Users } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const AdminUsersTab = ({ authToken, onMessage }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users`, { headers });
      if (res.ok) { const data = await res.json(); setUsers(data.users || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}? This cannot be undone.`)) return;
    setDeletingId(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, { method: 'DELETE', headers });
      if (res.ok) { onMessage(`Deleted ${email}`); setUsers(prev => prev.filter(u => (u.user_id || u.id) !== userId)); }
      else { const err = await res.json(); onMessage(err.detail || 'Failed to delete'); }
    } catch (e) { console.error(e); }
    finally { setDeletingId(null); }
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-xl font-bold text-white">All Users</h2><p className="text-white/50 text-sm">{users.length} registered accounts</p></div>
        <button onClick={fetchUsers} className="bg-white/10 hover:bg-white/15 text-white px-4 py-2 rounded-lg text-sm transition">Refresh</button>
      </div>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto" /></div>
      ) : users.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center"><Users className="w-10 h-10 text-white/20 mx-auto mb-3" /><p className="text-white/40">No users found</p></div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 text-white/40 text-xs font-medium uppercase tracking-wider">
            <div className="col-span-3">User</div><div className="col-span-3">Email</div><div className="col-span-2">Plan</div><div className="col-span-2">Type</div><div className="col-span-2 text-right">Actions</div>
          </div>
          {users.map((u) => {
            const uid = u.user_id || u.id;
            const isAdmin = u.is_admin || u.email === 'wnuv98.5@gmail.com';
            return (
              <div key={uid} className={`grid grid-cols-12 gap-3 items-center bg-white/5 border border-white/10 rounded-xl px-4 py-3 ${isAdmin ? 'border-purple-500/30' : ''}`} data-testid={`user-row-${uid}`}>
                <div className="col-span-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-purple-400 text-xs font-bold">{(u.name || '?')[0].toUpperCase()}</span>}
                  </div>
                  <span className="text-white text-sm font-medium truncate">{u.name || 'Unknown'}</span>
                </div>
                <div className="col-span-3 text-white/60 text-sm truncate">{u.email}</div>
                <div className="col-span-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.plan === 'premium' ? 'bg-amber-500/20 text-amber-400' : u.plan === 'professional' ? 'bg-purple-500/20 text-purple-400' : u.plan === 'starter' ? 'bg-blue-500/20 text-blue-400' : u.plan === 'byos' ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'}`}>{u.plan === 'premium' ? 'Premium' : (u.plan || 'none')}</span>
                </div>
                <div className="col-span-2"><span className={`text-xs ${u.user_type === 'station_owner' ? 'text-green-400' : 'text-white/40'}`}>{u.user_type || 'n/a'}</span></div>
                <div className="col-span-2 flex justify-end">
                  {isAdmin ? <span className="text-purple-400 text-xs font-medium">Admin</span> : (
                    <button onClick={() => deleteUser(uid, u.email)} disabled={deletingId === uid}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-3 py-1 rounded-lg text-xs transition disabled:opacity-40" data-testid={`delete-user-${uid}`}>
                      {deletingId === uid ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminUsersTab;
